import React, { useRef, useState, useEffect } from "react";
import { ambulanceServices } from "../../data/ambulanceServices";
import { FaChevronLeft, FaChevronRight, FaCircle } from "react-icons/fa";

function FadeIn({ children, direction = "left" }) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.30 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={
      direction === "left" ? `fade-in-left ${visible ? "visible" : ""}` :
      direction === "right" ? `fade-in-right ${visible ? "visible" : ""}` :
      direction === "bottom" ? `fade-in-bottom ${visible ? "visible" : ""}` : ""}
    >
      {children}
    </div>
  );
}

export default function OurServicesSection() {
  const scrollRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const cardWidth = 300 + 32; // card + gap

  // update scroll state + active index
  const checkScroll = () => {
    const node = scrollRef.current;
    if (!node) return;

    setAtStart(node.scrollLeft <= 2);
    setAtEnd(node.scrollLeft + node.offsetWidth >= node.scrollWidth - 2);

    // Find closest card
  const cards = Array.from(node.children);
  let closestIdx = 0;
  let minDiff = Infinity;

  cards.forEach((card, idx) => {
    const center = card.offsetLeft + card.offsetWidth / 2;
    const scrollCenter = node.scrollLeft + node.offsetWidth / 2;
    const diff = Math.abs(center - scrollCenter);
    if (diff < minDiff) {
      minDiff = diff;
      closestIdx = idx;
    }
  });

  setActiveIndex(closestIdx);
  };

  // useEffect(() => {
  //   const node = scrollRef.current;
  //   if (!node) return;
  //   checkScroll();
  //   node.addEventListener("scroll", checkScroll);
  //   window.addEventListener("resize", checkScroll);
  //   return () => {
  //     node.removeEventListener("scroll", checkScroll);
  //     window.removeEventListener("resize", checkScroll);
  //   };
  // }, []);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const handleScroll = () => requestAnimationFrame(checkScroll);
    node.addEventListener('scroll', handleScroll);
    return () => node.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollBy = (offset) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  const scrollToIndex = (index) => {
  const node = scrollRef.current;
  if (!node) return;

  const card = node.children[index];
  if (!card) return;

  const cardCenter = card.offsetLeft + card.offsetWidth / 2;
  const containerCenter = node.offsetWidth / 2;
  const targetScroll = cardCenter - containerCenter;

  node.scrollTo({
    left: targetScroll,
    behavior: "smooth",
  });
};

  return (
    <section className="w-full mx-auto bg-white px-2 sm:px-6 py-16 flex flex-col md:flex-row gap-6 
              items-stretch relative overflow-visible">
      {/* <FadeIn direction="left"> */}
      {/* Info block */}
      <div 
          className="w-full md:w-1/3 min-w-[240px] z-10 flex flex-col justify-center bg-white 
              bg-opacity-70 rounded-xl md:pr-10 px-4 py-4 md:py-14 items-center md:items-start text-center
               md:text-left" >
        <h2 className="text-4xl font-extrabold text-sky-600 mb-4">Our Services</h2>
        <div className="text-xl text-gray-800 font-semibold mb-2">
          Comprehensive ambulance support
        </div>
        <p className="text-gray-600">
          Fast, reliable ambulance transportation and assistance, 24/7. Certified staff,
          advanced vehicles, and total Karnataka coverage.
        </p>
        <p className="text-center text-gray-500 mb-6 text-sm py-4 sm:hidden">
          Swipe → to see more services
        </p>
      </div>
      {/* </FadeIn> */}

      {/* Slider block */}
      <div 
          className="md:w-2/3 relative">
        
        <FadeIn direction="right">
        {/* Cards container */}
        <div
            ref={scrollRef}
            className="overflow-x-auto scrollbar-hide scroll-smooth flex gap-8 px-12 py-2"
            style={{ scrollSnapType: "x mandatory" }}
        >
          {ambulanceServices.map((svc, idx) => (
            <div
                key={svc.title}
                className="min-w-[260px] sm:min-w-[300px] h-[480px] flex-shrink-0 bg-white bg-opacity-70 
                          rounded-2xl border shadow-lg  flex flex-col justify-end relative text-center 
                          p-4 select-none cursor-pointer"
                // className="flex-shrink-0 w-[260px] sm:w-[288px] h-[340px] mx-2 py-44 bg-white bg-opacity-70 rounded-2xl shadow-lg border p-6 flex flex-col items-center"
                style={{ 
                  scrollSnapAlign: "center",
                  backgroundImage: svc.image ? `url('${svc.image}')` : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <div className="absolute inset-x-0 bottom-0 pb-4 flex justify-center">
                  <div className="w-[230px] sm:w-[250px] h-auto bg-white bg-opacity-95 rounded-xl shadow 
                  text-center flex flex-col items-center justify-center border-2 border-yellow-300">
                    <div className="text-4xl mb-2 mt-2 flex justify-center items-center">{svc.icon}</div>
                    <div className="text-lg mb-4 font-bold text-sky-700 leading-tight">{svc.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Arrows at the bottom, centered (red box position), desktop only */}
          <div className="w-full flex items-center justify-center gap-8 py-4 hidden md:flex">
              <button
                  type="button"
                  className={`bg-gray-100 bg-opacity-70 rounded-full p-2 shadow hover:bg-opacity-90 
                            transition z-10 ${atStart ? 'opacity-30 cursor-not-allowed' : ''}`}
                  onClick={() => scrollBy(-cardWidth)}
                  aria-label="Scroll Left"
                  disabled={atStart}
              >
                <FaChevronLeft className="text-sky-600 text-xl" />
              </button>

            <FaCircle className="text-gray-200 text-sm" />

              <button
                  type="button"
                  className={`bg-gray-100 bg-opacity-70 rounded-full p-2 shadow hover:bg-opacity-90 transition z-10 ${atEnd ? 'opacity-30 cursor-not-allowed' : ''}`}
                  onClick={() => scrollBy(cardWidth)}
                  aria-label="Scroll Right"
                  disabled={atEnd}
            >
              <FaChevronRight className="text-sky-600 text-xl" />
            </button>
          </div>

          {/* Dots indicator - visible only on mobile */}
          <div className="flex justify-center mt-6 gap-2 md:hidden">
            {ambulanceServices.map((_, idx) => (
              <button
                key={idx}
                onClick={() => scrollToIndex(idx)}
                className={`w-3 h-3 rounded-full transition ${
                  idx === activeIndex ? "bg-sky-600 scale-110" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
